import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Edit, Trash2, Star, Clock } from 'lucide-react';
import { format } from 'date-fns';

// Event Dialog for adding/editing events
export function EventDialog({
  open,
  onOpenChange,
  selectedEvent,
  eventForm,
  setEventForm,
  eventTypeOptions,
  onSave
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {selectedEvent ? 'Modifier le service' : 'Ajouter un service'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Type *</Label>
            <Select 
              value={eventForm.type} 
              onValueChange={(v) => setEventForm({ ...eventForm, type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {eventTypeOptions.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Titre *</Label>
            <Input
              value={eventForm.title}
              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              placeholder="Ex: Dîner marocain, Transfer aéroport..."
              data-testid="event-title-input"
            />
          </div>
          
          <div>
            <Label>Description</Label>
            <Textarea
              value={eventForm.description}
              onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
              placeholder="Détails supplémentaires..."
              rows={2}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={eventForm.event_date}
                onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Heure</Label>
              <Input
                type="time"
                value={eventForm.event_time}
                onChange={(e) => setEventForm({ ...eventForm, event_time: e.target.value })}
              />
            </div>
          </div>
          
          <div>
            <Label>Nb personnes</Label>
            <Input
              type="number"
              min="1"
              value={eventForm.pax_count}
              onChange={(e) => setEventForm({ ...eventForm, pax_count: parseInt(e.target.value) || 1 })}
            />
          </div>
          
          <div>
            <Label>Notes</Label>
            <Textarea
              value={eventForm.notes}
              onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })}
              placeholder="Notes internes..."
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={onSave}>
            {selectedEvent ? 'Mettre à jour' : 'Ajouter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Events List Component
export function EventsList({
  events,
  eventTypeOptions,
  onAdd,
  onEdit,
  onDelete
}) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          Gérez les services liés à cette réservation (repas, activités, transferts...)
        </p>
        <Button size="sm" onClick={onAdd} data-testid="add-event-btn">
          <Plus className="w-4 h-4 mr-1" />
          Ajouter
        </Button>
      </div>
      
      {events.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
          <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Aucun service programmé</p>
          <p className="text-xs mt-1">Ajoutez des repas, activités ou transferts</p>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map(event => {
            const eventType = eventTypeOptions.find(t => t.value === event.type);
            const EventIcon = eventType?.icon || Star;
            
            return (
              <div 
                key={event.id} 
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg group hover:bg-muted"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${eventType?.color || 'bg-gray-100'}`}>
                    <EventIcon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-medium">{event.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {event.event_date && format(new Date(event.event_date), 'dd/MM/yyyy')}
                      {event.event_time && ` à ${event.event_time}`}
                      {event.pax_count && ` • ${event.pax_count} pers.`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8" 
                      onClick={() => onEdit(event)}
                    >
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-red-500" 
                      onClick={() => onDelete(event.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
